import React, { useState } from 'react';
import { ArrowLeft, Send, CheckCircle2, AlertCircle, Phone, Mail, MapPin, Clock, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BOARD_MEMBERS, CONTACT_INFO, EMERGENCY_CONTACT, ADMINISTRATION_CONTACT } from '../data';

export default function Contact() {
  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    tlf: '',
    emne: '',
    besked: '',
    website_url: '', // Honeypot field for anti-spam
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          navn: '',
          email: '',
          tlf: '',
          emne: '',
          besked: '',
          website_url: '',
        });
      } else {
        throw new Error('Der skete en fejl ved indsendelse af formularen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Kunne ikke indsende formularen. Kontroller din internetforbindelse.');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-950 hover:text-slate-600 transition">
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>

        {/* Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Kontakt & Bestyrelse
          </h1>
          <p className="mt-3 text-lg text-slate-500 max-w-3xl">
            Har du spørgsmål til din vandregning, vandkvaliteten eller andet? Du er altid velkommen til at kontakte os i vores kontortid eller sende os en besked via formularen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Contact Info & Form */}
          <div className="lg:col-span-7 space-y-12">
            {/* Quick Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3">
                <div className="text-slate-900">
                  <Clock className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">Kontortid</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Ring til os på tlf. <a href={`tel:${CONTACT_INFO.phoneOffice}`} className="font-semibold text-blue-600 hover:underline">{CONTACT_INFO.phoneOffice}</a>:
                </p>
                <div className="space-y-1 text-xs text-slate-600">
                  {CONTACT_INFO.openingHours.map((h) => (
                    <div key={h.day} className="flex justify-between border-b border-slate-100 pb-1">
                      <span>{h.day}</span>
                      <span className="font-semibold">{h.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3">
                <div className="text-slate-900">
                  <MapPin className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">Vandværket</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Fysisk adresse og postadresse:
                </p>
                <p className="text-xs text-slate-600 font-medium leading-relaxed">
                  {CONTACT_INFO.name}<br />
                  {CONTACT_INFO.address}<br />
                  {CONTACT_INFO.zipCity}
                </p>
                <div className="pt-2 text-xs text-slate-500">
                  E-mail: <a href={`mailto:${CONTACT_INFO.email}`} className="text-blue-600 hover:underline">{CONTACT_INFO.email}</a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Send os en besked</h3>
              <p className="text-sm text-slate-500 mb-6">
                Udfyld formularen herunder, så vender vi tilbage til dig hurtigst muligt.
              </p>

              {status === 'success' ? (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h4 className="mt-3 text-lg font-bold text-slate-900">Besked sendt!</h4>
                  <p className="mt-1.5 text-sm text-slate-600">
                    Mange tak for din henvendelse. Vi svarer dig så hurtigt som muligt.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-4 text-xs font-bold text-blue-600 hover:underline"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field (ABSOLUTELY HIDDEN from humans) */}
                  <div className="sr-only absolute -top-96 -left-96">
                    <label htmlFor="website_url">Efterlad dette felt tomt</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="navn" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Navn *</label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="tlf" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Telefon</label>
                      <input
                        type="tel"
                        id="tlf"
                        name="tlf"
                        value={formData.tlf}
                        onChange={handleChange}
                        placeholder="Dit telefonnummer"
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">E-mail *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Din e-mail adresse"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="emne" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Emne *</label>
                    <input
                      type="text"
                      id="emne"
                      name="emne"
                      required
                      value={formData.emne}
                      onChange={handleChange}
                      placeholder="Hvad drejer din henvendelse sig om?"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="besked" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Besked *</label>
                    <textarea
                      id="besked"
                      name="besked"
                      rows="4"
                      required
                      value={formData.besked}
                      onChange={handleChange}
                      placeholder="Skriv din besked her..."
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">
                      <AlertCircle className="h-5 w-5 shrink-0 text-rose-600" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md hover:bg-blue-500 hover:-translate-y-1 hover:shadow-lg disabled:bg-blue-400 transition-all duration-300 transform"
                  >
                    {status === 'loading' ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sender besked...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Besked
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right: Board Members & Administration */}
          <div className="lg:col-span-5 space-y-8">
            {/* Administration & Afregning */}
            <div className="space-y-4">
              <div className="flex items-center gap-2.5">
                <div className="h-1.5 w-8 rounded-full bg-blue-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-950">Administration & Afregning</span>
              </div>
              <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-3 hover:border-blue-300 transition bg-gradient-to-br from-white to-blue-50/20">
                <div className="flex items-center justify-between gap-4">
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-blue-600">
                    <User className="h-3.5 w-3.5" />
                    <span>{ADMINISTRATION_CONTACT.role}</span>
                  </span>
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-900 text-base">Bogholderi</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    {ADMINISTRATION_CONTACT.description}
                  </p>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2 border-t border-slate-100 text-xs text-slate-600">
                  <a href={`tel:${ADMINISTRATION_CONTACT.phone.replace(/\s+/g, '')}`} className="flex items-center gap-1 hover:text-blue-600 font-semibold transition">
                    <Phone className="h-3.5 w-3.5 text-blue-500" />
                    Tlf: {ADMINISTRATION_CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Bestyrelse & Ledelse */}
            <div className="space-y-4">
              <div className="flex items-center gap-2.5">
                <div className="h-1.5 w-8 rounded-full bg-slate-950" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-950">Bestyrelse & Ledelse</span>
              </div>

              <div className="space-y-4">
                {BOARD_MEMBERS.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-3 hover:border-blue-200 transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
                      <User className="h-3.5 w-3.5" color="#000000" stroke="#000000" />
                      <span className="text-slate-900">{member.role}</span>
                    </span>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-slate-900 text-base">{member.name}</h4>
                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {member.address}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2 border-t border-slate-100 text-xs text-slate-600">
                    {member.phone && (
                      <a href={`tel:${member.phone}`} className="flex items-center gap-1 hover:text-blue-600 transition">
                        <Phone className="h-3.5 w-3.5 text-slate-400" />
                        Tlf: {member.phone}
                      </a>
                    )}
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="flex items-center gap-1 hover:text-blue-600 transition break-all">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        {member.email}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>

        </div>

      </div>
    </div>
  );
}
